import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faCartShopping } from "@fortawesome/pro-solid-svg-icons";
import classNames from "classnames";
import { StatusCode } from "api/protocols";
import { GetFolder, IFolder } from "api/rpc/2024-04/facilityAdmin/product/folder";
import { GetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import { GetVariant } from "api/rpc/2024-04/facilityAdmin/product/variant";
import axios, { CancelToken } from "axios";
import { Badge } from "components/badge/Badge";
import { ButtonNew as Button } from "components/buttonNew";
import Input from "components/form/input";
import Icon from "components/icon/Icon";
import { faBarcodeRead, faTag } from "@fortawesome/pro-regular-svg-icons";
import Spin from "components/spin/spin";
import TableServiceFoldersMenu from "containers/facility/tableServiceFoldersMenu";
import RegistersModal from "elements/register/RegistersModal";
import { clearReaderDisplay } from "helpers/StripeTerminalWrapper";
import { printDesktopKitchenChit } from "helpers/Helpers";
import useModal from "hooks/modals/useModal";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useLocalStorage from "hooks/useLocalStorage";
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useHistory } from "react-router";
import { loadTableCart, postCartVariants, setActiveTable } from "redux/actions/cart";
import { loadDiscountOptions, loadPaymentOptions } from "redux/actions/facility";
import { showError } from "redux/actions/ui";
import { IOrder } from "redux/reducers/models/order";
import { IProduct } from "redux/reducers/models/product";
import { IRegister, IRegisterGroup } from "../../Admin";
import ProductsNew from "../../register/registerNew/ProductsNew";
import { useRegisterContext } from "../../register/registerNew/RegisterContext";
import { RegisterNotFound } from "../../register/registerNew/RegisterNew";
import {
  getStoredProductFolder,
  isStoredProductFolderFresh,
  upsertStoredProductFolder,
} from "helpers/productFoldersStorage";
import "./newTableService.scss";
import TableServiceCart from "./tableServiceCart/TableServiceCart";
import { useTranslation } from "react-i18next";
import { applyInventoryQuantitiesToProducts, fetchTrackedInventoryQuantities } from "helpers/registerInventory";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import { NavigationDropdownNew } from "components/navigationDropdownNew/NavigationDropdownNew";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import FlyToCartLayer, { IFlyToCartLayerHandle } from "./flyToCart/FlyToCartLayer";
import MessageDisplay from "components/messageDisplay/MessageDisplay";
import { buildFlyToCartProductVisual, getRectFromElement } from "./flyToCart/flyToCart";
import { PostKitchenChit } from "api/rpc/2024-04/facilityAdmin/facility/kitchen/chit";
import { IChit } from "hooks/usePOSPrinter/usePOSPrinter";
import { ITable } from "api/rpc/2024-04/facilityAdmin/tables/tables";

export interface ITableServiceState {
  folders: Array<IFolder>;
  selectedFolder: IFolder;

  /**List of products displayed */
  products: Array<IProduct>;
  /**Product search query string */
  productSearch: string;
  /**Message displayed when products are loading */
  productsLoadingMessage: string;
  /**Toggles barcode/sku search to auto-add variant to cart */
  barcodeSearch: boolean;

  order: IOrder;

  selectedCourseId: number;

  /**Message displayed when the table is loading */
  tableLoadingMessage: string;
  /**Message displayed when the cart (seat) is loading */
  cartLoadingMessage: string;
}

export default function NewTableService() {
  const {
    cartStore: { cart, activeTable },
    terminalStore,
    authStore,
    facilityStore,
  } = useAppSelector(store => store);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const register = useLocalStorage<IRegister>("register");
  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");
  const { addLoadingVariant, removeLoadingVariant } = useRegisterContext();
  const { t } = useTranslation();
  const { cancelToken, cancelRequest } = useCancelToken();
  const windowSize = useWindowSize();
  const isMobileOrBelow = (windowSize.width ?? MOBILE_WIDTH + 1) <= MOBILE_WIDTH;
  const [optimisticPendingCartAddCount, setOptimisticPendingCartAddCount] = useState(0);
  const [cartOpen, setCartOpen] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= MOBILE_WIDTH,
  );
  const [cartTransitionEnabled, setCartTransitionEnabled] = useState(false);
  const flyToCartRef = useRef<IFlyToCartLayerHandle>(null);
  const mobileCartButtonRef = useRef<HTMLDivElement>(null);
  const cartLineItemsTargetRef = useRef<HTMLDivElement>(null);
  const productSearchInputRef = useRef<HTMLDivElement>(null);

  const handleProductAdded = (payload: { product: IProduct; sourceElement: HTMLElement | null }) => {
    const sourceRect = getRectFromElement(payload.sourceElement);
    const targetElement = cartOpen
      ? cartLineItemsTargetRef.current
      : mobileCartButtonRef.current;
    const targetRect = getRectFromElement(targetElement);

    if (!sourceRect || !targetRect) {
      return;
    }

    flyToCartRef.current?.trigger({
      rects: { sourceRect, targetRect },
      product: buildFlyToCartProductVisual(
        payload.product,
        facilityStore?.teeSheetSettings?.show_product_images,
      ),
    });
  };

  useEffect(() => {
    if (windowSize.width == null) {
      return;
    }
    if (!isMobileOrBelow) {
      setCartOpen(false);
      setCartTransitionEnabled(false);
    }
  }, [isMobileOrBelow, windowSize.width]);

  const [state, setState] = useState<ITableServiceState>({
    //Folders
    folders: null,
    selectedFolder: null,

    //Products
    products: null,
    productSearch: "",
    productsLoadingMessage: "",
    barcodeSearch: false,

    //Order
    order: null,

    selectedCourseId: null,

    tableLoadingMessage: "",
    cartLoadingMessage: "",
  });

  const { state: registerModal, updateModal: updateRegisterModal, closeModal: closeRegisterModal } = useModal();

  useEffect(() => {
    if (activeTable) {
      //Set the cart in redux as the first cart returned on the table
      void loadFirstSeatCart();
      //Load payment options and discount options if values in redux are empty
      if (facilityStore?.paymentOptions?.length === 0) {
        void dispatch(loadPaymentOptions());
      }
      if (facilityStore?.discountOptions?.length === 0) {
        void dispatch(loadDiscountOptions());
      }
    } else {
      void navigateToTableSelection();
    }
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    setState(prevState => ({
      ...prevState,
      tableLoadingMessage: t("secure.facility.table_service.new_table_service.new_table_service.001"),
    }));
    if (registerGroup) {
      void loadFolders(registerGroup?.id, source.token);
    } else {
      setState(prevState => ({ ...prevState, tableLoadingMessage: "" }));
    }
    return () => source.cancel();
  }, [registerGroup]);

  // Used for searching all products
  useEffect(() => {
    const source = axios.CancelToken.source();
    let mounted = true;
    let timeoutId: number = null;
    const search = () => {
      timeoutId = window.setTimeout(() => {
        void (() => {
          try {
            if (state.productSearch === "") {
              if (mounted) {
                setState(prevState => ({ ...prevState, products: null, productsLoadingMessage: "" }));
              }
              return;
            } else {
              if (mounted) {
                void loadProducts(source.token);
              }
            }
          } catch (error) {
            console.log("err", error);
          }
          return;
        })();
      }, 500);
    };
    search();
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      source.cancel();
    };
  }, [state.productSearch]);

  function loadFirstSeatCart() {
    const firstCart = activeTable?.carts?.length > 0 ? activeTable?.carts[0] : null;
    ReactDOM.unstable_batchedUpdates(() => {
      setState(prevState => ({ ...prevState, order: firstCart ? firstCart?.order : null }));
      void dispatch(loadTableCart({ tableCart: firstCart }, false));
    });
  }

  function getUnsentKitchenLineItemIds(table: ITable): number[] {
    let lineItemIds: Array<number> = [];
    if (table?.carts) {
      table.carts.forEach(tableCart => {
        if (tableCart?.status !== "complete" && (!tableCart?.order || tableCart?.order?.financial_status === "unpaid")) {
          const lineItemsToAdd =
            tableCart?.line_items
              ?.filter(
                filteredLineItem =>
                  !filteredLineItem.parent_id &&
                  filteredLineItem?.kitchen_status !== "sent" &&
                  (filteredLineItem?.product?.meta?.kitchen_location_id ||
                    filteredLineItem?.product?.meta?.kitchen_location_id_2 ||
                    filteredLineItem?.product?.meta?.kitchen_location_id_3),
              )
              ?.map(lineItem => lineItem?.id) ?? [];
          lineItemIds = [...lineItemIds, ...lineItemsToAdd];
        }
      });
    }
    return lineItemIds;
  }

  async function sendKitchenChitsOnLeave(table: ITable) {
    const lineItemIds = getUnsentKitchenLineItemIds(table);
    if (lineItemIds.length === 0) {
      return;
    }

    const res = await PostKitchenChit({ table_id: table.id, line_item_ids: lineItemIds }, false);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error sending kitchen chits")));
      return;
    }

    const chits = res?.data;
    let chitCount = 0;
    const tasks = [];

    for (const chit of chits) {
      chit.chit_number = ++chitCount;
      chit.total_chits = chits.length;
      tasks.push(printDesktopKitchenChit(chit as IChit, t));
    }

    Promise.all(tasks).catch((error: string) => dispatch(showError(error)));
  }

  async function navigateToTableSelection() {
    if (facilityStore?.teeSheetSettings?.table_service_send_chits && activeTable?.id) {
      await sendKitchenChitsOnLeave(activeTable);
    }
    if (terminalStore?.reader) {
      void clearReaderDisplay();
    }
    dispatch(setActiveTable(null));
    history.push("/admin/table-selection");
  }

  async function loadProducts(cancelToken: CancelToken) {
    const searchQuery = state.productSearch;
    setState(prevState => ({
      ...prevState,
      productsLoadingMessage: t("secure.facility.table_service.new_table_service.new_table_service.002"),
    }));
    //Search products
    if (!state.barcodeSearch) {
      const productRes = await GetProduct({ extended: true, search: searchQuery }, false, cancelToken);
      setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
      if (productRes.status === StatusCode.OK) {
        setState(prevState => ({ ...prevState, products: productRes?.data }));
      } else {
        if (cancelToken && cancelToken?.reason) {
          setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
          return;
        }
        dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.003")));
      }
    } else {
      //Cannot scan and add item to cart if an order is already in progress (partial payment made)
      if (state?.order) {
        dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.004")));
        setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
        return;
      }
      //Search variant by barcode and add to cart
      const res = await GetVariant({ extended: true, barcode: state.productSearch }, false, cancelToken);
      setState(prevState => ({
        ...prevState,
        productsLoadingMessage: t("secure.facility.table_service.new_table_service.new_table_service.005"),
      }));
      if (res.status === StatusCode.OK) {
        const variants = res?.data?.filter(variant => variant.facility_access === true);

        if (variants.length == 1) {
          const variant = variants[0];
          void addLoadingVariant(variant, variant?.product);
          const updatedCart = await dispatch(
            postCartVariants(
              { cart_id: cart?.id, variants: [{ variant_id: variant?.id, quantity: 1, parent_id: null }] },
              false,
            ),
          );
          setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
          void removeLoadingVariant(variant?.id);
          if (!updatedCart) {
            dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.006")));
            return;
          }
          if (isMobileOrBelow) {
            handleProductAdded({
              product: variant.product,
              sourceElement: productSearchInputRef.current,
            });
          }
          setState(prevState => ({ ...prevState, productSearch: "" }));
        } else {
          setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
          dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.007")));
        }
        setState(prevState => ({ ...prevState, products: [] }));
      } else {
        setState(prevState => ({ ...prevState, productsLoadingMessage: null }));
        if (cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.008")));
      }
    }
  }

  async function loadFolders(registerGroupId: number, cancelToken: CancelToken) {
    const folderRes = await GetFolder({ register_group_id: registerGroupId, extended: false }, false, cancelToken);

    if (folderRes.status !== StatusCode.OK) {
      if (cancelToken && cancelToken.reason) {
        return;
      }
      dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.009")));
      setState(prevState => ({ ...prevState, tableLoadingMessage: "" }));
      return;
    }

    const folders = folderRes.data;

    // Get default folder for the selected Register or the default set for the facility
    const defaultFolderId = Number(localStorage.getItem("default_folder_id"));
    let selectedFolder: IFolder = undefined;
    if (defaultFolderId > 0) {
      selectedFolder = folders.find((folder: IFolder) => folder.id === defaultFolderId);
    } else {
      selectedFolder = folders.find((folder: IFolder) => folder.default === true);
    }

    if (!selectedFolder) {
      selectedFolder = folders?.[0];
    }

    setState(prevState => ({
      ...prevState,
      folders: folders,
      tableLoadingMessage: "",
    }));

    if (selectedFolder) {
      void handleChangeSelectedFolder(selectedFolder);
    }
  }

  async function handleChangeSelectedFolder(selectedFolder: IFolder) {
    if (selectedFolder?.id === undefined) {
      return;
    }

    void cancelRequest();

    const storedProductFolder = await getStoredProductFolder(selectedFolder.id);

    if (isStoredProductFolderFresh(selectedFolder, storedProductFolder)) {
      setState(prevState => ({ ...prevState, selectedFolder: storedProductFolder }));
      void loadInventoryLevels(storedProductFolder, cancelToken());
    } else {
      const folderRes = await GetFolder({ id: selectedFolder.id, extended: true }, true);

      if (folderRes.status !== StatusCode.OK || folderRes.data?.length === undefined || folderRes.data.length === 0) {
        dispatch(showError(t("secure.facility.table_service.new_table_service.new_table_service.009")));
        return;
      }

      const newSelectedFolder = folderRes.data[0];
      await upsertStoredProductFolder(newSelectedFolder);

      setState(prevState => ({ ...prevState, selectedFolder: newSelectedFolder }));
      void loadInventoryLevels(newSelectedFolder, cancelToken());
    }
  }

  function renderProducts() {
    if (state.productSearch && state.products) {
      const products = state.products?.sort((firstProd: IProduct, secondProd: IProduct) => {
        if (firstProd.title < secondProd.title) {
          return -1;
        } else if (firstProd.title > secondProd.title) {
          return 1;
        } else {
          return 0;
        }
      }) as [];
      return { useGridPositions: false, products };
    } else {
      const products = state.selectedFolder?.products ?? ([] as []);
      return { useGridPositions: true, products };
    }
  }

  function handleBarcodeSearchClick() {
    const productSearchId = document.getElementById("productSearchId");
    productSearchId.focus();
    setState(prevState => ({ ...prevState, barcodeSearch: !prevState.barcodeSearch }));
  }

  function setMobileCartOpen(open: boolean) {
    setCartTransitionEnabled(true);
    setCartOpen(open);
  }

  function handleProductSearchChange(event: React.ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    if (isMobileOrBelow && cartOpen && !state.barcodeSearch && next.length > 0) {
      setMobileCartOpen(false);
    }
    setState(prevState => ({
      ...prevState,
      productSearch: next,
      ...(!state.barcodeSearch && next.length > 0 ? { products: null } : {}),
    }));
  }

  function handleRegisterChange(register?: IRegister) {
    // Close register modal
    void closeRegisterModal();

    // Set selected register in localStorage
    if (register) {
      localStorage.setItem("register", JSON.stringify(register));
      localStorage.setItem("default_folder_id", JSON.stringify(register.default_folder_id));
      localStorage.setItem("register_group", JSON.stringify(register.register_group));
    }
  }

  async function loadInventoryLevels(folder: IFolder, cancelToken: CancelToken) {
    let productIds: Array<number> = [];
    let variantIds: Array<number> = [];
    const selectedFolder = { ...folder };
    selectedFolder?.products?.forEach(product => {
      //Only take variant ids of product that has track_inventory set to true
      if (product?.track_inventory && product?.variants?.length > 0) {
        const productVariantIds = product?.variants?.map(variant => variant?.id);
        productIds = [...productIds, product?.id];
        variantIds = [...variantIds, ...productVariantIds];
      }
    });
    if (variantIds?.length > 0) {
      const { status, getQuantity } = await fetchTrackedInventoryQuantities(variantIds, false, cancelToken);
      if (status !== StatusCode.OK) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting inventory details")));
        return;
      }
      const storedProductFolder = await getStoredProductFolder(selectedFolder?.id);
      if (storedProductFolder && productIds?.length > 0) {
        const updatedProducts = applyInventoryQuantitiesToProducts(
          storedProductFolder.products ?? [],
          productIds,
          getQuantity,
        );
        const updatedFolder = { ...storedProductFolder, products: updatedProducts };
        await upsertStoredProductFolder(updatedFolder);
        setState(prev => ({ ...prev, selectedFolder: updatedFolder }));
      }
    }
  }

  const cartLineItemCount =
    (cart?.line_items?.filter(lineItem => !lineItem?.parent_id)?.length ?? 0) + optimisticPendingCartAddCount;
  const activeSeatIndex = activeTable?.carts?.findIndex(seatCart => seatCart?.token === cart?.token) ?? 0;
  const isProductSearchActive = !!state.productSearch && !state.barcodeSearch;
  const isProductSearchLoading =
    isProductSearchActive && (state.products === null || !!state.productsLoadingMessage);
  const productRenderData = renderProducts();
  const folderProductCount = state.selectedFolder?.products?.length ?? 0;
  const canCreateProduct =
    facilityStore?.facility?.permissions?.products_create && authStore?.user?.permissions?.products_create;
  const foldersReady = state.folders !== null;
  const isFolderProductsLoading = foldersReady && state.folders.length > 0 && state.selectedFolder == null;
  const showSearchPulse = isProductSearchLoading && folderProductCount > 0;
  const showSearchSpinner = isProductSearchLoading && folderProductCount === 0;
  const showProductsLoading = isFolderProductsLoading || showSearchSpinner;
  const showNoResults =
    isProductSearchActive &&
    !isProductSearchLoading &&
    Array.isArray(state.products) &&
    state.products.length === 0;
  const showNoProducts =
    !isProductSearchActive &&
    !showProductsLoading &&
    !state.productsLoadingMessage &&
    foldersReady &&
    (state.folders.length === 0 || (state.selectedFolder != null && folderProductCount === 0));
  const createNewProductAction = canCreateProduct
    ? {
        label: t("Create new product"),
        action: () => history.push("/admin/product/new"),
        type: "primary" as const,
      }
    : null;

  function handleClearProductSearch() {
    setState(prevState => ({ ...prevState, productSearch: "", products: null, productsLoadingMessage: "" }));
    document.getElementById("productSearchId")?.focus();
  }

  function handleSearchProduct() {
    if (isMobileOrBelow) {
      setMobileCartOpen(false);
    }
    document.getElementById("productSearchId")?.focus();
  }

  return (
    <div
      className={classNames("new-table-service", {
        "new-table-service--cart-open": cartOpen,
        "new-table-service--cart-animated": cartTransitionEnabled,
      })}
    >
      <div className="new-table-service-top-bar">
        <div className="new-table-service-top-bar-left">
          <Button onClick={() => void navigateToTableSelection()} size="medium" type="text">
            <FontAwesomeIcon className="new-table-service-back-icon" icon={["far", "arrow-left"]} />
            <span className="new-table-service-back-text">
              {t("secure.facility.table_service.new_table_service.new_table_service.011")}
            </span>
          </Button>
        </div>
        <div className="new-table-service-top-bar-center">
          <div className="new-table-service-top-bar-center-table-details">
            <p className="table-primary-title">{activeTable?.title}</p>
            {cart ? (
              <p className="table-secondary-title">
                {t("secure.facility.table_service.table_selection.011")} {activeSeatIndex + 1}
              </p>
            ) : null}
          </div>
          <div className="table-product-search-input" ref={productSearchInputRef}>
            <Input
              id="productSearchId"
              value={state.productSearch}
              onChange={handleProductSearchChange}
              type="search"
              placeholder={t("Search products...")}
              labelHidden
              disabled={!!state?.tableLoadingMessage || !registerGroup}
              trailingButtons={[
                <Button key={1} type="secondary" active={state.barcodeSearch} onClick={handleBarcodeSearchClick}>
                  <Icon icon={faBarcodeRead} size="medium" />
                </Button>,
              ]}
            />
          </div>
        </div>
        <div className="new-table-service-top-bar-right">
          {terminalStore?.reader ? (
            <div className="new-table-service-top-bar-right-terminal">
              <Badge type="success" outline size="medium" iconLeft={<FontAwesomeIcon icon={faCircle} />}>
                {terminalStore?.reader?.label}
              </Badge>
            </div>
          ) : null}
          <div className="new-table-service-top-bar-right-user-container">
            <div className="new-table-service-top-bar-right-user-details">
              <p className="table-primary-title">{authStore?.user?.full_name}</p>
              <p
                className="table-secondary-title table-title-selectable"
                onClick={() => updateRegisterModal({ isOpen: true })}
              >
                {register && register?.title ? register?.title : "No Register"}
              </p>
            </div>
            <div className="new-table-service-top-bar-right-user-menu">
              <NavigationDropdownNew
                rightAlign
                hideChevron
                showPlainTextLabel
                label={
                  <div className="table-user-icon-container">
                    <div className="table-user-icon-inner">
                      {authStore?.user?.first_name?.charAt(0)}
                      {authStore?.user?.last_name?.charAt(0)}
                    </div>
                  </div>
                }
                sections={[
                  [
                    {
                      label: authStore?.user?.full_name,
                      disabled: true,
                      onClick: () => {},
                    },
                  ],
                  [
                    {
                      label: register?.title ? register.title : "No Register",
                      onClick: () => updateRegisterModal({ isOpen: true }),
                    },
                  ],
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      {state?.tableLoadingMessage ? (
        <div style={{ display: "flex", height: "100%" }}>
          <div className="text-center m-auto">
            <div style={{ height: "32px" }}>
              <Spin />
            </div>
            <div>{state?.tableLoadingMessage}</div>
          </div>
        </div>
      ) : (
        <div className="new-table-service-main-container">
          <div className="new-table-service-main-container-left">
            <TableServiceFoldersMenu
              selectedFolder={state.selectedFolder}
              setSelectedFolder={folder => {
                void handleChangeSelectedFolder(folder as IFolder);
              }}
              folders={state.folders}
            />
          </div>
          <div className="new-table-service-main-container-center">
            {!registerGroup ? (
              <RegisterNotFound t={t} />
            ) : (
              <>
                <div className="new-table-service-products-container">
                  {showNoResults ? (
                    <div className="new-table-service-products-empty">
                      <MessageDisplay
                        centered
                        icon="magnifying-glass"
                        title={t("No results found")}
                        content={t("Please try a different search, or create a new product.")}
                        actions={[
                          {
                            label: t("Clear search"),
                            action: handleClearProductSearch,
                            type: "secondary",
                          },
                          ...(createNewProductAction ? [createNewProductAction] : []),
                        ]}
                      />
                    </div>
                  ) : showNoProducts ? (
                    <div className="new-table-service-products-empty">
                      <MessageDisplay
                        centered
                        icon="circle-exclamation"
                        title={t("Search products")}
                        content={t("Use the search bar to add a product into the cart.")}
                        actions={[
                          {
                            label: t("Search product"),
                            action: handleSearchProduct,
                            type: "primary",
                          },
                        ]}
                      />
                    </div>
                  ) : showProductsLoading ? (
                    <div className="new-table-service-products-loading">
                      <div className="text-center m-auto">
                        <div style={{ height: "32px" }}>
                          <Spin />
                        </div>
                        <div>
                          {isFolderProductsLoading
                            ? t("secure.facility.table_service.new_table_service.new_table_service.002")
                            : state.productsLoadingMessage ||
                              t("secure.facility.table_service.new_table_service.new_table_service.002")}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={classNames("new-table-service-products-grid", {
                        "new-table-service-products-grid--search-loading": showSearchPulse,
                      })}
                    >
                      <ProductsNew
                        {...productRenderData}
                        disabled={
                          !cart ||
                          cart?.status === "complete" ||
                          state?.order?.financial_status === "partially_paid" ||
                          cart?.status === "void" ||
                          !!state.cartLoadingMessage ||
                          showSearchPulse
                        }
                        cart={cart}
                        selectedFolder={state.selectedFolder}
                        productsLoadingMessage={state?.productsLoadingMessage}
                        selectedCourseId={state.selectedCourseId}
                        onProductAdded={isMobileOrBelow ? handleProductAdded : undefined}
                        optimisticCartAdd={isMobileOrBelow}
                        onOptimisticPendingCartAddCountChange={setOptimisticPendingCartAddCount}
                      />
                    </div>
                  )}
                </div>
                <div className="new-table-service-folders-container-horizontal">
                  <TableServiceFoldersMenu
                    selectedFolder={state.selectedFolder}
                    setSelectedFolder={folder => {
                      void handleChangeSelectedFolder(folder as IFolder);
                    }}
                    folders={state.folders}
                  />
                </div>
              </>
            )}
          </div>
          <div className="new-table-service-main-container-right">
            <TableServiceCart
              order={state.order}
              selectCourse={id =>
                setState(prevState => ({
                  ...prevState,
                  selectedCourseId: id === prevState.selectedCourseId ? null : id,
                }))
              }
              selectedCourseId={state.selectedCourseId}
              setTableState={setState}
              navigateToTableSelection={navigateToTableSelection}
              cartLoadingMessage={state.cartLoadingMessage}
              selectedFolder={state?.selectedFolder}
              isOverlay={isMobileOrBelow && cartOpen}
              mobileSeatBar={isMobileOrBelow}
              allowLineItemAutoScroll={!isMobileOrBelow || cartOpen}
              flyToCartTargetRef={cartLineItemsTargetRef}
              onSearchProduct={handleSearchProduct}
            />
          </div>
        </div>
      )}
      {isMobileOrBelow && !state?.tableLoadingMessage ? (
        <>
          <FlyToCartLayer ref={flyToCartRef} />
          <div className="new-table-service-mobile-nav">
          <Button
            className="new-table-service-mobile-nav-item"
            type={!cartOpen ? "primary" : "secondary"}
            size="medium"
            aria-current={!cartOpen ? "page" : undefined}
            onClick={() => setMobileCartOpen(false)}
          >
            <FontAwesomeIcon icon={faTag} />
            <span className="new-table-service-mobile-nav-label">
              {t("Products")}
            </span>
          </Button>
          <div ref={mobileCartButtonRef} className="new-table-service-mobile-nav-cart-target">
          <Button
            className="new-table-service-mobile-nav-item"
            type={cartOpen ? "primary" : "secondary"}
            size="medium"
            aria-current={cartOpen ? "page" : undefined}
            onClick={() => setMobileCartOpen(true)}
          >
            <FontAwesomeIcon icon={faCartShopping} />
            <span className="new-table-service-mobile-nav-label">
              {t("Cart")}
            </span>
            {cartLineItemCount > 0 ? (
              <span className="new-table-service-mobile-nav-count">
                {cartLineItemCount}
              </span>
            ) : null}
          </Button>
          </div>
        </div>
        </>
      ) : null}
      {/* Modal for updating the selected register */}
      <RegistersModal open={registerModal?.isOpen} onClose={register => handleRegisterChange(register)} />
    </div>
  );
}
